import { ref } from "vue";
import { Facade } from "@/drawing";
import { Logger } from "@/logger";
import Settings from "./Settings";
import CanvasSettingsState from "./CanvasSettingsState";
import { SaveState, saveDrawing } from "./SaveDrawingState";
import { MemoryMonitor } from "@/utils/MemoryMonitor";
import { useLayersList } from "@/composable/useLayersList";
import {
  findTheme,
  themeBackgroundHex,
  type PlanBackground,
} from "@/utils/planBackground";

/**
 * Fond du plan (sombre ou clair) changé depuis l'outil dessin, sans rouvrir
 * le fichier ni recharger l'app (cf. DRDrawingViewer.applyBackgroundColorLive).
 * Le réglage reste celui des paramètres généraux (Settings.actviveTheme) :
 * il n'est enregistré qu'une fois le fond appliqué, pour ne jamais diverger
 * de l'écran.
 */

/** Changement en cours : le plan est redessiné, l'écran est bloqué. */
export const isApplyingPlanBackground = ref(false);

/** L'indicateur s'affiche avant la régénération, qui bloque le fil principal. */
const RENDER_DELAY_MS = 100;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * - « applied » : fond appliqué et enregistré (ou seulement enregistré quand
 *   aucun plan n'est ouvert : il le prendra à son ouverture) ;
 * - « skipped » : rien fait (changement déjà en cours, plan quitté entre-temps) ;
 * - « failed » : erreur du moteur, réglage inchangé.
 */
export type PlanBackgroundResult = "applied" | "skipped" | "failed";

export async function setPlanBackground(
  name: PlanBackground,
): Promise<PlanBackgroundResult> {
  const theme = findTheme(name);
  if (!theme || isApplyingPlanBackground.value) return "skipped";
  const color = themeBackgroundHex(theme);

  if (!isDrawingOpen()) {
    if (name !== Settings.actviveTheme.name) Settings.setActiveTheme(name);
    return "applied";
  }
  // Déjà affiché. Comparé au moteur, pas au réglage : après un échec, le
  // fond d'origine doit rester sélectionnable.
  if (
    name === Settings.actviveTheme.name &&
    Facade.view.getBackgroundColor() === color
  ) {
    return "applied";
  }

  isApplyingPlanBackground.value = true;
  // Le plan affiché au départ : quitté entre-temps, on n'y touche plus.
  const view = Facade.view;
  const isSamePlan = () => isDrawingOpen() && Facade.view === view;
  try {
    await wait(RENDER_DELAY_MS);
    if (!isSamePlan()) return "skipped";

    // Déplacer, copier, pivoter… en cours : ses fantômes et ses originaux
    // masqués ne survivraient pas à la régénération. On l'annule proprement.
    const tool = Facade.tools.current;
    if (tool?.hasPendingPlacement()) tool.cancelPendingPlacement();

    // Filet : la régénération est l'opération la plus lourde en mémoire. Les
    // modifications en attente sont d'abord enregistrées, si l'utilisateur a
    // choisi l'enregistrement automatique.
    if (Settings.autoSaveOn && SaveState.value === "changed") {
      await saveDrawing();
      if (!isSamePlan()) return "skipped";
      // Sauvegarde ratée ou différée : pas de régénération sans ce filet.
      if (SaveState.value === "changed") return "failed";
    }

    if (!applyToEngine(color)) return "failed";
    Settings.setActiveTheme(name);
    refreshAfterBackground();
    return "applied";
  } catch (error) {
    Logger.captureException(error, { source: "setPlanBackground", name });
    return "failed";
  } finally {
    isApplyingPlanBackground.value = false;
  }
}

/**
 * Fond appliqué au moteur. Les calques de la légende encore en noir ou blanc
 * fixe passent d'abord en ACI 7 : la régénération les dessine ainsi dans la
 * bonne couleur. En cas d'échec, retour au fond d'origine, au mieux.
 */
function applyToEngine(color: number): boolean {
  const previous = Facade.view.getBackgroundColor();
  try {
    MemoryMonitor.suppressReportingFor(10_000);
    Facade.noteConfig.refreshForegroundLayers();
    Facade.view.applyBackgroundColor(color);
    return true;
  } catch (error) {
    Logger.captureException(error, { source: "setPlanBackground", color });
    try {
      Facade.view.applyBackgroundColor(previous);
    } catch {
      // Moteur hors d'usage : le plan sera rouvert avec le réglage inchangé.
    }
    return false;
  }
}

/**
 * Après le changement : légende recorrigée comme à l'ouverture d'un plan,
 * dernier rendu (calque actif réécrit, désélection en espace papier), liste
 * des calques à jour. Un échec ici n'annule pas le fond, déjà appliqué.
 */
function refreshAfterBackground(): void {
  try {
    CanvasSettingsState.reapplyThemeToLegend();
    Facade.view.update();
    useLayersList().updateLayers();
  } catch (error) {
    Logger.captureException(error, { source: "refreshAfterBackground" });
  }
}

function isDrawingOpen(): boolean {
  return Facade.isInitialized && Facade.file.isOpen();
}
